import React, { createContext, useState, useEffect, useRef, useCallback, useContext, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getNotificationToken } from '../services/api';
import { NotificationEvent, NotificationType } from '../types/notifications';
import { stripTokenParam } from '../utils/resetToken';
import { AuthContext } from '../components/auth/AuthContext';
import config from '../config';

const MAX_NOTIFICATIONS = 50;
const DEBOUNCE_MS = 500;
const RECONNECT_DELAY_MS = 5000;

// Events that only tell open pages to look again. They carry no news, so they
// reach subscribers but never the history list or the unread badge — a panel
// row for one would say nothing the user needs to read.
const REFRESH_ONLY_TYPES: ReadonlySet<NotificationType> = new Set<NotificationType>([
  'contact_status_changed',
]);

type SubscribeCallback = (event: NotificationEvent) => void;

interface NotificationContextType {
  notifications: NotificationEvent[];
  unreadCount: number;
  clearUnread: () => void;
  clearAll: () => void;
  subscribe: (types: NotificationType[], callback: SubscribeCallback) => () => void;
  connected: boolean;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  clearUnread: () => {},
  clearAll: () => {},
  subscribe: () => () => {},
  connected: false,
});

export const useNotifications = () => useContext(NotificationContext);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { accessToken } = useContext(AuthContext);
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  const subscribersRef = useRef<Map<string, { types: NotificationType[]; callback: SubscribeCallback }>>(new Map());
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const lastEventIdRef = useRef<string | null>(null);
  // Ids we already accepted. Reconnect resumes from lastEventIdRef, so the
  // server can legitimately re-send the frame it was cut off on — and a
  // duplicated "7 new opportunities ready" reads as a second day's delivery.
  // Held in insertion order and trimmed to the same window as the list below,
  // so it can never outgrow what we retain.
  const seenIdsRef = useRef<Set<string>>(new Set());

  const subscribe = useCallback((types: NotificationType[], callback: SubscribeCallback): (() => void) => {
    const id = uuidv4();
    subscribersRef.current.set(id, { types, callback });

    return () => {
      subscribersRef.current.delete(id);
      const timer = debounceTimersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        debounceTimersRef.current.delete(id);
      }
    };
  }, []);

  const notifySubscribers = useCallback((event: NotificationEvent) => {
    subscribersRef.current.forEach(({ types, callback }, id) => {
      if (types.includes(event.notificationType)) {
        const existing = debounceTimersRef.current.get(id);
        if (existing) clearTimeout(existing);

        const timer = setTimeout(() => {
          callback(event);
          debounceTimersRef.current.delete(id);
        }, DEBOUNCE_MS);

        debounceTimersRef.current.set(id, timer);
      }
    });
  }, []);

  const handleSSEMessage = useCallback((messageEvent: MessageEvent) => {
    try {
      const data = JSON.parse(messageEvent.data);

      // Track position for replay on reconnect
      if (messageEvent.lastEventId) {
        lastEventIdRef.current = messageEvent.lastEventId;
      }

      const notification: NotificationEvent = {
        ...data,
        id: messageEvent.lastEventId || uuidv4(),
        receivedAt: new Date(),
        read: false,
      };

      // Drop a replay before it touches any state: the badge and the
      // subscribers must agree with the list, so the check has to gate all
      // three rather than live inside the list updater.
      if (seenIdsRef.current.has(notification.id)) return;

      // Not recorded in seenIdsRef either: that set mirrors the retained list
      // and is trimmed with it, so a refresh id would push a real row's id
      // out early. A replayed refresh only costs one more refetch.
      if (REFRESH_ONLY_TYPES.has(notification.notificationType)) {
        notifySubscribers(notification);
        return;
      }

      setNotifications(prev => {
        const updated = [notification, ...prev];
        return updated.slice(0, MAX_NOTIFICATIONS);
      });
      setUnreadCount(prev => prev + 1);
      notifySubscribers(notification);

      seenIdsRef.current.add(notification.id);
      if (seenIdsRef.current.size > MAX_NOTIFICATIONS) {
        // Sets iterate in insertion order, so this drops the oldest id —
        // exactly the one the list just evicted.
        seenIdsRef.current.delete(seenIdsRef.current.values().next().value as string);
      }
    } catch (error) {
      console.error('Failed to parse SSE message:', error);
    }
  }, [notifySubscribers]);

  // Everything the provider holds is scoped to one signed-in identity, and
  // admin impersonation swaps accessToken without a reload. Carried over, the
  // next user would see the previous one's notifications and unread badge —
  // and, since the replay guard landed, a genuine event of theirs whose stream
  // id happened to match a seen one would be silently dropped as a duplicate.
  // Declared before the connection effect so the reset lands first.
  useEffect(() => {
    seenIdsRef.current.clear();
    lastEventIdRef.current = null;
    setNotifications([]);
    setUnreadCount(0);
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    let active = true;

    const connect = async () => {
      if (!active) return;
      try {
        const { token: sseToken } = await getNotificationToken();
        if (!active) return;

        const baseUrl = config.baseURL;
        let url = `${baseUrl}/api/v1/notifications/stream?token=${sseToken}`;
        if (lastEventIdRef.current) {
          url += `&lastEventId=${encodeURIComponent(lastEventIdRef.current)}`;
        }

        if (eventSourceRef.current) {
          console.log('[SSE] Closing previous EventSource before reconnect');
          eventSourceRef.current.close();
        }

        // Never log the raw url: the SSE token rides in its query string and
        // is bearer-equivalent, and Sentry turns console output into
        // breadcrumbs. Its scrubber (services/monitoring.ts) only rewrites
        // breadcrumb.data.url, not the message, so the token would reach a
        // third party. Same rule the reset-password token already follows.
        console.log('[SSE] Creating EventSource:', stripTokenParam(url));
        const es = new EventSource(url);

        es.onopen = () => {
          console.log('[SSE] Connection opened, readyState:', es.readyState);
          setConnected(true);
        };

        es.onmessage = handleSSEMessage;

        es.onerror = (e) => {
          console.log('[SSE] Error, readyState:', es.readyState, 'active:', active, 'isCurrent:', eventSourceRef.current === es);
          setConnected(false);
          es.close();
          if (eventSourceRef.current === es) {
            eventSourceRef.current = null;
          }
          if (active) {
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
          }
        };

        eventSourceRef.current = es;
      } catch (error) {
        console.error('[SSE] Failed to establish connection:', error);
        if (active) {
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      }
    };

    connect();

    return () => {
      active = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      // The rule's advice — snapshot `debounceTimersRef.current` into a local
      // at the top of the effect — would be a no-op today and a bug tomorrow.
      // The Map is built once by `useRef(new Map())` and is only ever mutated
      // (.set/.get/.delete above); nothing in this file assigns `.current`, so
      // reading it in cleanup reaches the same Map the SSE handlers write into.
      // A snapshot would silently stop doing that the day someone reassigns it,
      // leaking the pending debounce timers past unmount.
      debounceTimersRef.current.forEach(timer => clearTimeout(timer));
      // eslint-disable-next-line react-hooks/exhaustive-deps
      debounceTimersRef.current.clear();
    };
  }, [accessToken, handleSSEMessage]);

  const clearUnread = useCallback(() => {
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, clearUnread, clearAll, subscribe, connected }}>
      {children}
    </NotificationContext.Provider>
  );
};
